import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';

import StarfieldBackground from './StarfieldBackground';

// Always use the current domain for API calls
const API_BASE_URL = window.location.origin;

export default function Payment() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paying, setPaying] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Get order_id from URL (support both orderId and order_id)
  const params = new URLSearchParams(location.search);
  const orderId = params.get('orderId') || params.get('order_id');

  useEffect(() => {
    async function fetchOrder() {
      setLoading(true);
      setError('');
      const { data, error } = await supabase.from('orders').select('*').eq('id', orderId).single();
      if (error || !data) {
        setError('Order not found.');
        setLoading(false);
        return;
      }
      setOrder(data);
      setLoading(false);
    }
    if (orderId) fetchOrder();
    else {
      setError('No order ID found');
      setLoading(false);
    }
  }, [orderId]);

  const handlePayWithCredits = async () => {
    setPaying(true);
    setError('');
    setShowConfirm(false); // Hide confirmation modal

    if (!user || !order) {
      setError('User or order not found.');
      setPaying(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/pay-with-credits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderId: order.id,
          userId: user.id
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccess(true);
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        // Use the specific error message from the server
        setError(result.message || 'An unknown error occurred.');
      }
    } catch (err) {
      console.error('Payment API call failed:', err);
      setError('Could not connect to the server. Please try again later.');
    } finally {
      setPaying(false);
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  useEffect(() => {
    console.log('Auth loading:', authLoading, 'User:', user);
  }, [authLoading, user]);

  if (authLoading || loading) return <><StarfieldBackground /><div style={{color:'#fff', textAlign:'center', marginTop:60}}>Loading order...</div></>;
  if (!user) return <><StarfieldBackground /><div style={{color:'#ff4757', textAlign:'center', marginTop:60}}>You must be signed in to pay for your order.</div></>;
  if (error) return <><StarfieldBackground /><div style={{color:'#ff4757', textAlign:'center', marginTop:60}}>{error} <br/> <button onClick={() => navigate('/dashboard')} style={{marginTop:18}}>Go to Dashboard</button></div></>;

  const total = parseFloat(order.total);
  const originalTotal = parseFloat(order.screenshot_total);
  const subtotal = parseFloat(order.subtotal) || 41.00; // Default to 41.00 if not available
  const tipAmount = parseFloat(order.tip) || 0;
  const amountSaved = !isNaN(originalTotal) && !isNaN(total)
    ? (originalTotal - total) + tipAmount
    : 0;
  const savingsPercent = amountSaved > 0 && !isNaN(originalTotal)
    ? Math.round((amountSaved / originalTotal) * 100)
    : 0;

  const canPay = !isNaN(total) && total > 0 && order.status !== 'paid';

  // Terminal-style block styles
  const terminalStyle = {
    background: 'transparent',
    color: '#00ff00',
    textAlign: 'left',
    padding: '16px',
    borderRadius: '8px',
    fontFamily: '"Source Code Pro", monospace',
    fontSize: '1.2em',
    lineHeight: '1.4',
    marginBottom: 24,
    whiteSpace: 'pre',
    overflowX: 'auto'
  };

  return (
    <>
      <StarfieldBackground />
      <div style={{maxWidth: 440, margin: '64px auto', borderRadius: 18, padding: '48px 36px 36px 36px', position: 'relative', textAlign: 'center'}}>
        <h2 style={{color:'#fff', fontSize: '2em', paddingBottom: '16px', marginBottom: '24px', borderBottom: '1px solid rgba(255,255,255,0.2)'}}>Payment</h2>
        {(!canPay || !order.total) ? (
          <>
            <div style={{color:'#fff', marginBottom: 24}}>Order total not available yet. Please refresh.</div>
            <button onClick={handleRefresh} style={{background:'#3498db', color:'#fff', fontWeight:600, border:'none', borderRadius:8, padding:'12px 32px', fontSize:'1.08em', cursor:'pointer'}}>Refresh</button>
          </>
        ) : success ? (
          <div style={{color:'#4caf50', fontWeight:600, fontSize:'1.1em'}}>Payment successful! Order status updated.<br/>You will be redirected to the dashboard.</div>
        ) : (
          <>
            <pre style={terminalStyle}>{`
> SUBTOTAL      : ${subtotal.toFixed(2)}
> ORIGINAL TOTAL: ${isNaN(originalTotal) ? '--' : originalTotal.toFixed(2)}
> YOUR PRICE    : ${total.toFixed(2)}
> TIP           : ${tipAmount.toFixed(2)}
> YOU SAVED     : ${amountSaved.toFixed(2)} (${savingsPercent}%)
`}</pre>
            <button onClick={() => setShowConfirm(true)} disabled={paying} style={{background:'transparent', color:'#fff', fontWeight:600, border:'1px solid #fff', borderRadius:8, padding:'12px 32px', fontSize:'1.08em', cursor:'pointer'}}>
              {paying ? 'Processing...' : 'Pay with Credits'}
            </button>
            {showConfirm && (
              <div style={{position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.35)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center'}}>
                <div style={{background:'rgba(35, 39, 47, 0.75)', backdropFilter: 'blur(10px)', borderRadius:14, padding:'36px 28px 28px 28px', minWidth:320, maxWidth:'95vw', boxShadow:'0 6px 32px rgba(0,0,0,0.12)', position:'relative', textAlign:'center'}}>
                  <h3 style={{color:'#fff'}}>Confirm Payment</h3>
                  <div style={{color:'#fff', marginBottom: 18}}>
                    Are you sure you want to pay <span style={{
                      background: 'rgba(0,0,0,0.25)',
                      border: '1px solid rgba(255, 224, 130, 0.5)',
                      borderRadius: '8px',
                      padding: '6px 14px',
                      margin: '0 6px',
                      color: '#fff',
                      fontWeight: 600,
                      display: 'inline-block'
                    }}>${order.total}</span> with credits?
                  </div>
                  <div style={{display:'flex', gap:12, justifyContent:'center'}}>
                    <button onClick={handlePayWithCredits} disabled={paying} style={{background:'transparent', color:'#fff', fontWeight:600, border:'1px solid #fff', borderRadius:8, padding:'10px 32px', fontSize:'1.08em', cursor:'pointer'}}>Yes, Pay</button>
                    <button onClick={() => setShowConfirm(false)} style={{background:'transparent', color:'#bbb', fontWeight:600, border:'1px solid #bbb', borderRadius:8, padding:'10px 32px', fontSize:'1.08em', cursor:'pointer'}}>Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
} 